"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useLeakDetectorData = void 0;
const react_1 = require("react");
const queries_1 = require("../../graphql/queries");
const amplifyBootstrap_1 = require("../../utils/amplifyBootstrap");
const humidityDataUtils_1 = require("../../utils/humidity/humidityDataUtils");
const DEFAULT_DAYS = 30;
/**
 * Transforms SensorUsageRollup data into DailyLeakDetectorData format
 * @param leakDetectorData - Array of sensor usage rollup data
 * @param latestHumidityFromEvent - Latest humidity value from sensor events (for most recent hour)
 */
const transformLeakDetectorData = (leakDetectorData, latestHumidityFromEvent = null) => {
    const transformedData = leakDetectorData.map((dailyData) => {
        const leakDetectorRollup = dailyData.leakDetectorRollup;
        return {
            label: dailyData.date,
            hour: dailyData.hour || 0,
            temperature: leakDetectorRollup?.avgTemperature || 0,
            humidity: leakDetectorRollup?.avgHumidity || 0,
            leakDetected: leakDetectorRollup?.leakDetected || false,
            leakCount: leakDetectorRollup?.leakCount || 0,
            leakPercentage: leakDetectorRollup?.leakPercentage || 0,
        };
    });
    if (transformedData.length > 0 && latestHumidityFromEvent !== null) {
        const sortedData = [...transformedData].sort((a, b) => {
            const dateA = new Date(a.label);
            const dateB = new Date(b.label);
            if (dateA.getTime() !== dateB.getTime()) {
                return dateB.getTime() - dateA.getTime();
            }
            return b.hour - a.hour;
        });
        const mostRecentIndex = transformedData.findIndex((item) => item.label === sortedData[0].label && item.hour === sortedData[0].hour);
        if (mostRecentIndex !== -1) {
            transformedData[mostRecentIndex] = {
                ...transformedData[mostRecentIndex],
                humidity: latestHumidityFromEvent,
            };
        }
    }
    return transformedData;
};
/**
 * Creates a complete 24-hour timeline and maps existing rollup data to it.
 * Missing hours are filled with default values (0 humidity, no leak).
 */
const createComplete24HourTimeline = (rollupData, endTimestamp) => {
    // Create 24-hour timeline ending at the specified timestamp or current time
    const now = endTimestamp ? new Date(endTimestamp * 1000) : new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    // Create array of 24 hourly slots
    const timelineSlots = [];
    for (let i = 0; i < 24; i++) {
        const slotTime = new Date(twentyFourHoursAgo.getTime() + i * 60 * 60 * 1000);
        slotTime.setMinutes(0, 0, 0); // Round to exact hour
        timelineSlots.push({
            label: slotTime.toISOString().split('T')[0],
            hour: slotTime.getHours(),
            temperature: 0,
            humidity: 0,
            leakDetected: false,
            leakCount: 0,
            leakPercentage: 0,
        });
    }
    // Create a map of existing rollup data for quick lookup
    const rollupDataMap = new Map();
    rollupData.forEach((data) => {
        const key = `${data.label}-${data.hour}`;
        rollupDataMap.set(key, data);
    });
    // Map timeline slots to actual data or defaults
    return timelineSlots.map((slot) => {
        const key = `${slot.label}-${slot.hour}`;
        const existingData = rollupDataMap.get(key);
        // Return existing data if available, otherwise return the default slot
        return existingData || slot;
    });
};
/**
 * Hook that provides leak detector data based on SensorUsageRollup records
 * @param params - Parameters for fetching leak detector data
 * @returns Leak detector data with daily metrics
 */
const useLeakDetectorData = ({ sensorId, hubId, days = DEFAULT_DAYS, enabled = true, lastReportedTimestamp, }) => {
    const [data, setData] = (0, react_1.useState)(null);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const fetchInProgressRef = (0, react_1.useRef)(false);
    const latestHumidityFromEvent = (0, humidityDataUtils_1.useLatestHumidityFromEvents)(hubId, sensorId);
    const endDate = (0, react_1.useMemo)(() => {
        // End at the date of the latest event timestamp if available, otherwise current date
        const date = lastReportedTimestamp ? new Date(lastReportedTimestamp * 1000) : new Date();
        return date.toISOString().split('T')[0];
    }, [lastReportedTimestamp]);
    const startDate = (0, react_1.useMemo)(() => {
        // If we have lastReportedTimestamp, start from that date minus days
        // Otherwise start from current date minus days
        const date = lastReportedTimestamp ? new Date(lastReportedTimestamp * 1000) : new Date();
        date.setDate(date.getDate() - days);
        return date.toISOString().split('T')[0];
    }, [days, lastReportedTimestamp]);
    const fetchLeakDetectorData = (0, react_1.useCallback)(async () => {
        if (!enabled || !sensorId) {
            return;
        }
        if (fetchInProgressRef.current) {
            return;
        }
        fetchInProgressRef.current = true;
        setLoading(true);
        setError(null);
        try {
            const result = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensorUsageRollup,
                variables: {
                    sensorId,
                    startDate,
                    endDate,
                },
            });
            const rawData = result?.data?.getSensorUsageRollup;
            const sensorUsageData = Array.isArray(rawData) ? rawData : (rawData?.items || []);
            if (sensorUsageData && sensorUsageData.length > 0) {
                const transformedData = transformLeakDetectorData(sensorUsageData, latestHumidityFromEvent);
                // Create complete 24-hour timeline with gaps filled
                const completeTimelineData = createComplete24HourTimeline(transformedData, lastReportedTimestamp);
                // Use setTimeout to ensure state update is properly batched
                setTimeout(() => {
                    setData(completeTimelineData);
                }, 0);
            }
            else {
                // Even with no data, create a complete 24-hour timeline with all zeros
                const emptyTimelineData = createComplete24HourTimeline([], lastReportedTimestamp);
                setTimeout(() => {
                    setData(emptyTimelineData);
                }, 0);
            }
        }
        catch (err) {
            console.error('Error fetching leak detector data:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch leak detector data');
            setData(null);
        }
        finally {
            fetchInProgressRef.current = false;
            setLoading(false);
        }
    }, [enabled, sensorId, startDate, endDate, latestHumidityFromEvent]);
    const refetch = async () => {
        await fetchLeakDetectorData();
    };
    (0, react_1.useEffect)(() => {
        fetchLeakDetectorData();
    }, [fetchLeakDetectorData]);
    return {
        data,
        loading,
        error,
        refetch,
    };
};
exports.useLeakDetectorData = useLeakDetectorData;
